import type { SyntheticEvent } from 'react';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { tracker } from 'app-components/utils/core';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';

import SettingsIcon from 'assets/icons/nav-icons/settings.inline.svg';
import { settingsTabs, type SettingsTabs } from 'containers/Settings/settingsTabs';

const POPOVER_WIDTH = 320;

const useStyles = makeStyles()((theme) => ({
  settingsPopover: {
    minWidth: POPOVER_WIDTH,
    padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  },
  listItems: {
    padding: 0,
  },
}));

type TopBarSettingsProps = {
  id?: string;
};

const TopBarSettings = memo(function TopBarSettings({ id }: TopBarSettingsProps) {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const featureFlags = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state));
  const a2aTransfer = useAppSelector(a2aTransfersSelectors.a2aTransferEnabled);

  const currentSettingKey = useMemo<SettingsTabs | null>(() => {
    const match = location.pathname.match(/^\/settings(?:\/([^/]+))?/);
    if (!match) {
      return null;
    }
    const raw = match[1] ? decodeURIComponent(match[1]) : 'general';
    return raw in settingsTabs ? (raw as SettingsTabs) : null;
  }, [location.pathname]);

  const isEnabled: Record<string, boolean> = useMemo(
    () => ({
      always: true,
      accountEnabled: featureFlags.get('accountsManagement'),
      categoriesTagsEnabled: true,
      financesEnabled: featureFlags.get('finances') && !featureFlags.get('sharingEnabled'),
      recurringManagement: featureFlags.get('recurringManagement'),
      notificationPreferences: featureFlags.get('notificationPreferences'),
      securitiesEnabled: featureFlags.get('securitiesSettings'),
      sharingEnabled: featureFlags.get('sharingEnabled'),
      businessEnabled: featureFlags.get('businessEnabled'),
      transactionsSettings: featureFlags.get('transactionsSettings'),
      a2aTransferEnabled: a2aTransfer,
      transactionRules: true,
    }),
    [featureFlags, a2aTransfer],
  );

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleItemClick = useCallback(
    (settingKey: SettingsTabs) => {
      if (settingKey === 'Recurring') {
        tracker.track(tracker.events.viewRecurring, { source: 'settings' });
      }
      navigate(`/settings/${encodeURIComponent(settingKey)}`);
      closeMenu();
    },
    [navigate, closeMenu],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <Tooltip title='Settings'>
        <IconButton
          variant='tonal'
          shape='rounded'
          id={id ?? 'topbar-settings'}
          size='medium'
          aria-label='Settings'
          aria-haspopup='menu'
          aria-expanded={!!anchorEl}
          onClick={(e: SyntheticEvent) => setAnchorEl(e.currentTarget)}
        >
          <Box
            component='span'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              '& > svg': {
                display: 'block',
                flexShrink: 0,
              },
            }}
          >
            <SettingsIcon width={20} height={20} />
          </Box>
        </IconButton>
      </Tooltip>
      <Popover
        id='settings-menu'
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        slotProps={{
          paper: {
            elevation: 24,
            style: {
              width: POPOVER_WIDTH,
              transform: 'translate3d(0, 0, 0)',
            },
          },
        }}
      >
        <div className={classes.settingsPopover}>
          <Typography variant='titleMedium' sx={{ paddingX: 2, paddingBottom: 1 }}>
            Settings
          </Typography>
          <MenuList className={classes.listItems}>
            {(Object.keys(settingsTabs) as SettingsTabs[]).map((settingKey) => {
              const setting = settingsTabs[settingKey];
              if (!isEnabled[setting.allow]) return null;
              const isCurrent = currentSettingKey === settingKey;
              return (
                <MenuItem
                  key={settingKey}
                  id={`${settingKey.toLowerCase()}-settings-menu-item`}
                  onClick={() => handleItemClick(settingKey)}
                  sx={{
                    '&:hover .MuiListItemIcon-root, &:hover .MuiTypography-root': {
                      color: 'primary.main',
                    },
                    ...(isCurrent && {
                      bgcolor: 'action.selected',
                      '& .MuiListItemIcon-root, & .MuiTypography-root': {
                        color: 'primary.main',
                      },
                    }),
                  }}
                  selected={isCurrent}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{setting.icon()}</ListItemIcon>
                  <ListItemText
                    primary={setting.name}
                    slotProps={{
                      primary: {
                        variant: 'paragraphRegular',
                      },
                    }}
                  />
                </MenuItem>
              );
            })}
          </MenuList>
        </div>
      </Popover>
    </>
  );
});

export default TopBarSettings;
